import { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@material-ui/core';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { usePatientSearch } from 'circulator/hooks/epic/usePatientSearch';
import { useNotifier } from 'common/hooks/useNotifier';
import Message from 'common/models/Message';
import type * as api from 'common/types/api';

interface Props {
    open: boolean;
    onClose: () => void;
    facilityId: number;
    onConfirm: (patientEpicId: string, patientInitials: string) => void;
}

function getInitials(fullName: string): string {
    const nameParts = fullName.split(' ');

    if (nameParts.length === 0) {
        return '';
    }

    return nameParts.map(part => part.charAt(0).toUpperCase()).join('');
}

export const EpicPatientFindDialog: React.FC<Props> = ({ open, onClose, facilityId, onConfirm }) => {
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<api.ListEpicPatientsResponse[]>([]);
    const [date, setDate] = useState<Date | null>(null);
    const [givenName, setGivenName] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [selectedPatientEpicId, setSelectedPatientEpicId] = useState('');
    const [selectedPatientInitials, setSelectedPatientInitials] = useState('');
    const { search } = usePatientSearch();
    const { notify } = useNotifier();

    const handleClose = () => {
        onClose();
    };

    const handleSearch = async () => {
        setIsSearching(true);
        setSearchResults([]);
        setSelectedPatientEpicId('');
        setSelectedPatientInitials('');

        try {
            const results = await search({
                patientGivenName: givenName,
                patientFamilyName: familyName,
                patientDob: date ?? new Date(),
                facilityId: facilityId,
            });

            console.log('Search results:', results);
            setSearchResults(results.filter(item => item?.patientEpicId !== undefined && item?.patientEpicId !== null && item?.patientEpicId !== ''));
        } catch (error) {
            console.error('Error searching for Epic patients', error);
            notify(new Message({ title: 'Error searching for Epic patients.', type: 'error' }));
        }

        setIsSearching(false);
    };

    const handleConfirm = () => {
        // Automatically select only row in DataGrid if needed
        if (selectedPatientEpicId === '' && searchResults.length === 1) {
            onConfirm(searchResults[0].patientEpicId, getInitials(searchResults[0].patientFullName));
        } else {
            onConfirm(selectedPatientEpicId, selectedPatientInitials);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'patientFullName',
            headerName: 'Full Name',
            flex: 0.4,
            sortable: true,
            hideSortIcons: false,
            disableColumnMenu: true,
        },
        {
            field: 'patientDob',
            headerName: 'DOB',
            flex: 0.2,
            sortable: true,
            hideSortIcons: false,
            disableColumnMenu: true,
        },
        {
            field: 'patientEpicId',
            headerName: 'Epic ID',
            flex: 0.4,
            sortable: false,
            hideSortIcons: true,
            disableColumnMenu: true,
        },
    ];

    return (
        <Dialog open={open} fullWidth maxWidth="lg" aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle>
                <Typography variant="h6" component="h6" display="block" gutterBottom>
                    Find Epic Patient
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <TextField
                            id="givenName"
                            name="givenName"
                            value={givenName}
                            onChange={event => setGivenName(event.target.value)}
                            label="First Name"
                            variant="outlined"
                            margin="none"
                            fullWidth
                            required
                            autoComplete="First Name"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            id="familyName"
                            name="familyName"
                            value={familyName}
                            onChange={event => setFamilyName(event.target.value)}
                            label="Last Name"
                            variant="outlined"
                            margin="none"
                            fullWidth
                            required
                            autoComplete="Last Name"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                name="dob"
                                label="Date of Birth"
                                value={date}
                                onChange={newValue => setDate(newValue)}
                                disableFuture
                                sx={{ width: '100%' }}
                                slotProps={{ textField: { required: true } }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={1}>
                        <Button onClick={handleSearch} variant="contained" color="primary" size="medium" disabled={isSearching}>
                            Search
                            {isSearching && (
                                <>
                                    &nbsp;&nbsp;
                                    <CircularProgress size={15} thickness={8} color="primary" />
                                </>
                            )}
                        </Button>
                    </Grid>
                </Grid>
                <DataGrid
                    loading={isSearching}
                    rows={searchResults || []}
                    getRowId={(row: api.ListEpicPatientsResponse) => row.patientEpicId}
                    onRowSelectionModelChange={newSelectedPatientEpicIds => {
                        if (newSelectedPatientEpicIds.length === 0) {
                            setSelectedPatientEpicId('');
                            setSelectedPatientInitials('');
                            return;
                        }

                        const newSelectedPatientEpicId = newSelectedPatientEpicIds[0].toString();

                        setSelectedPatientEpicId(newSelectedPatientEpicId);

                        const data = searchResults.find(item => item.patientEpicId === newSelectedPatientEpicId);

                        if (data) {
                            setSelectedPatientInitials(getInitials(data.patientFullName));
                        }
                    }}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                            },
                        },
                    }}
                    pageSizeOptions={[10]}
                    //hideFooterSelectedRowCount={true}
                    sx={{ bgcolor: 'background.paper', marginTop: '16px' }}
                    autoHeight
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined" size="medium" className="modal-cancel-button">
                    Cancel
                </Button>
                <Button autoFocus onClick={handleConfirm} variant="contained" size="medium" disabled={isSearching}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};
